import { useState, useEffect, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";

const DOT_COLORS = [
  "rgba(10,10,10,0.25)", // corner TL
  "rgba(10,10,10,0.55)", // cardinal T
  "rgba(10,10,10,0.25)", // corner TR
  "rgba(10,10,10,0.55)", // cardinal L
  "#E84393",             // center - pink
  "rgba(10,10,10,0.55)", // cardinal R
  "rgba(10,10,10,0.25)", // corner BL
  "rgba(10,10,10,0.55)", // cardinal B
  "rgba(10,10,10,0.25)", // corner BR
];

const PHASE_OFFSETS = [0, 0.1, 0.2, 0.15, 0.05, 0.25, 0.3, 0.2, 0.35];

export function LoadingScreen() {
  const [visible, setVisible] = useState(true);
  const [status, setStatus] = useState<"ASSETS" | "VIDEO" | "READY">("ASSETS");
  const [exiting, setExiting] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const mousePos = useRef({ x: 0, y: 0 });
  const rafId = useRef(0);
  const mountTime = useRef(Date.now());

  // Status progression + exit logic
  useEffect(() => {
    const t1 = setTimeout(() => setStatus("VIDEO"), 150);

    const heroVideo = document.querySelector<HTMLVideoElement>("video");
    let videoHandler: (() => void) | null = null;

    const tryReady = () => {
      setStatus("READY");
      const elapsed = Date.now() - mountTime.current;
      const remaining = Math.max(0, 400 - elapsed);
      setTimeout(() => setExiting(true), remaining);
    };

    if (heroVideo) {
      if (heroVideo.readyState >= 1) {
        setTimeout(tryReady, 160);
      } else {
        videoHandler = () => tryReady();
        heroVideo.addEventListener("loadedmetadata", videoHandler, { once: true });
        heroVideo.addEventListener("canplay", videoHandler, { once: true });
      }
    }

    // Hard fallback
    const fallback = setTimeout(() => {
      setStatus("READY");
      setExiting(true);
    }, 2000);

    return () => {
      clearTimeout(t1);
      clearTimeout(fallback);
      if (heroVideo && videoHandler) {
        heroVideo.removeEventListener("loadedmetadata", videoHandler);
        heroVideo.removeEventListener("canplay", videoHandler);
      }
    };
  }, []);

  // Mouse parallax via rAF
  const onMouseMove = useCallback((e: MouseEvent) => {
    mousePos.current = { x: e.clientX, y: e.clientY };
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);

    const tick = () => {
      if (gridRef.current) {
        const cx = window.innerWidth / 2;
        const cy = window.innerHeight / 2;
        const dx = (mousePos.current.x - cx) / cx; // -1..1
        const dy = (mousePos.current.y - cy) / cy;
        const tx = dx * 8;
        const ty = dy * 8;
        const rot = dx * 0.5;
        gridRef.current.style.transform = `translate(${tx}px, ${ty}px) rotate(${rot}deg)`;

        // Proximity brightening
        const dots = gridRef.current.children;
        for (let i = 0; i < dots.length; i++) {
          const dot = dots[i] as HTMLElement;
          const rect = dot.getBoundingClientRect();
          const dotCx = rect.left + rect.width / 2;
          const dotCy = rect.top + rect.height / 2;
          const dist = Math.hypot(mousePos.current.x - dotCx, mousePos.current.y - dotCy);
          const boost = dist < 120 ? 0.15 : 0;
          dot.style.setProperty("--boost", String(boost));
        }
      }
      rafId.current = requestAnimationFrame(tick);
    };
    rafId.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      cancelAnimationFrame(rafId.current);
    };
  }, [onMouseMove]);

  // Remove from DOM after exit
  useEffect(() => {
    if (exiting) {
      const t = setTimeout(() => setVisible(false), 280);
      return () => clearTimeout(t);
    }
  }, [exiting]);

  if (!visible) return null;

  return (
    <motion.div
      initial={{ opacity: 1 }}
      animate={{ opacity: exiting ? 0 : 1 }}
      transition={{ duration: 0.26, ease: "easeInOut" }}
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 99999, backgroundColor: "#F3F1EC" }}
    >
      {/* Dot grid */}
      <div
        ref={gridRef}
        className="grid grid-cols-3 gap-[10px]"
        style={{ willChange: "transform" }}
      >
        {DOT_COLORS.map((color, i) => {
          const isPink = i === 4;
          return (
            <span
              key={i}
              className="block rounded-full"
              style={{
                width: 6,
                height: 6,
                backgroundColor: color,
                opacity: `calc(1 + var(--boost, 0))`,
                animation: `dotPulse${isPink ? "Pink" : ""} 900ms ease-in-out ${PHASE_OFFSETS[i] * 900}ms infinite`,
              }}
            />
          );
        })}
      </div>

      {/* Thin rule line */}
      <div
        className="absolute"
        style={{
          width: 40,
          height: 1,
          backgroundColor: "rgba(10,10,10,0.12)",
          top: "calc(50% + 28px)",
          left: "50%",
          transform: "translateX(-50%)",
        }}
      />

      {/* Micro text bottom-left */}
      <div
        className="absolute bottom-6 left-6 select-none"
        style={{ fontFamily: "monospace", letterSpacing: "0.12em" }}
      >
        <div style={{ fontSize: 9, color: "rgba(10,10,10,0.45)" }}>
          ALABAMA AI — STUDIO SYSTEM
        </div>
        <div style={{ fontSize: 9, color: "rgba(10,10,10,0.35)", marginTop: 2 }}>
          LOADING
        </div>
        <div
          style={{
            fontSize: 9,
            color: status === "READY" ? "#E84393" : "rgba(10,10,10,0.3)",
            marginTop: 2,
            transition: "color 0.3s",
          }}
        >
          {status}
        </div>
      </div>

      {/* CSS keyframes */}
      <style>{`
        @keyframes dotPulse {
          0%, 100% { transform: scale(1); opacity: 0.25; }
          50% { transform: scale(1.25); opacity: 0.7; }
        }
        @keyframes dotPulsePink {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.3); opacity: 1; }
        }
      `}</style>
    </motion.div>
  );
}
